import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { OrderStage, TrackerStepState } from '@entities/search-session';
import { useMe } from '@entities/user/api';
import { useAuthDialog } from '@features/auth';
import { ROUTES } from '@shared/config/routes';
import { Button, Container, Logo } from '@shared/ui';

import { AccountMenu } from './AccountMenu';
import styles from './AppHeader.module.css';
import { LanguageMenu } from './LanguageMenu';
import { StepTracker } from './StepTracker';

interface AppHeaderProps {
  stage: OrderStage;
  paid: boolean;
  /**
   * Трекер скрывается на экранах вне воронки (список заказов): показывать шаг
   * «какого-то» заказа было бы враньём о состоянии.
   */
  showTracker?: boolean | undefined;
  onNavigate?: ((step: TrackerStepState) => void) | undefined;
  /** Есть ли у пройденного шага экран — см. `StepTracker`. */
  canNavigate?: ((step: TrackerStepState) => boolean) | undefined;
}

/**
 * Тёмная шапка приложения (DESIGN_SYSTEM §4.3, редизайн 2026-10): логотип,
 * трекер семи шагов, выбор языка и вход или меню аккаунта.
 *
 * Бейджа «Sicher & datensparsam» и статуса Karl в шапке больше нет (решение
 * заказчика по макетам 2026-10): состояние заказа показывает трекер, а Karl
 * присутствует в рельсе — там же стоит и его обязательная маркировка AI.
 *
 * Правая часть — та же, что у публичной шапки: язык переключается на любом
 * экране, а вход открывает модальное окно, не уводя со страницы.
 */
export function AppHeader({
  stage,
  paid,
  showTracker = true,
  onNavigate,
  canNavigate,
}: AppHeaderProps) {
  const { t } = useTranslation();
  const { data: session } = useMe();

  return (
    <header className={styles.header}>
      <Container
        width="app"
        className={[styles.inner, showTracker && styles.withTracker].filter(Boolean).join(' ')}
      >
        {/* Знак ведёт на публичную главную — так же, как в шапке публичных
            страниц: одно и то же нажатие не должно означать разное в
            зависимости от того, на каком экране человек стоит. В список
            заказов ведут «Meine Bestellungen» в меню аккаунта и в подвале и ссылка
            возврата на самих экранах воронки. */}
        <Link to={ROUTES.home} className={styles.brand} aria-label={t('brand.name')}>
          <Logo inverse />
        </Link>

        {showTracker ? (
          <nav className={styles.tracker} aria-label={t('app.tracker.label')}>
            <StepTracker
              stage={stage}
              paid={paid}
              onNavigate={onNavigate}
              canNavigate={canNavigate}
            />
          </nav>
        ) : (
          <span className={styles.spacer} />
        )}

        <div className={styles.side}>
          <LanguageMenu />
          {session ? <AccountMenu user={session.user} inverse /> : <LoginButton />}
        </div>
      </Container>
    </header>
  );
}

/**
 * «Anmelden» гостя. Отдельный компонент не ради разметки: окно входа живёт в
 * `AuthProvider`, и его хук вызывается только там, где кнопка действительно
 * нужна, — у вошедшего пользователя оболочке провайдер не обязателен.
 */
function LoginButton() {
  const { t } = useTranslation();
  const { requestAuth } = useAuthDialog();

  return (
    <Button
      variant="inverse"
      className={styles.login}
      onClick={() => {
        // Модальное окно, а не переход на /login: гостевой заказ и экран, на
        // котором человек стоит, никуда не деваются (MVP_PRD §8, §41).
        requestAuth();
      }}
    >
      {t('nav.login')}
    </Button>
  );
}
